import { useEffect, useState } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'app/components/ui/command'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'
import { PickerButton } from './PickerButton'

export type OwnerOption = { id: string; name: string }

/** SMC only. Staff or users depending on the staff dashboard toggle, mirroring the contact drawer. */
export function OwnerPicker({
  value,
  onChange,
  useUsers,
}: {
  value?: OwnerOption
  onChange: (owner?: OwnerOption) => void
  useUsers: boolean
}) {
  const [search, setSearch] = useState(``)
  const [options, setOptions] = useState<OwnerOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    bg(
      gqlFetchList<{ id: string; name?: string }>(
        useUsers ? `users` : `staffMembers`,
        { search: search ? `name:*${search}*` : undefined, pagination: { page: 0, limit: 20 } },
        `id name`,
      )
        .then((res) => {
          if (cancelled) return
          setOptions(res.hits.flatMap((hit) => (hit.name ? [{ id: hit.id, name: hit.name }] : [])))
          setLoading(false)
        })
        .catch((err) => logWarn(`OwnerPicker: owner lookup failed`, err)),
    )
    return () => {
      cancelled = true
    }
  }, [search, useUsers])

  return (
    <PickerButton label={value?.name ?? `Unassigned`} muted={!value} contentClassName="w-[280px] p-0">
      {(close) => (
        <Command shouldFilter={false} className="bg-surface">
          <CommandInput placeholder="Search…" value={search} onValueChange={setSearch} />
          <CommandList>
            {loading ? (
              <div className="p-3 text-center text-body-sm text-subtle">Loading…</div>
            ) : options.length === 0 ? (
              <CommandEmpty>No matches</CommandEmpty>
            ) : (
              <CommandGroup>
                {value && (
                  <CommandItem
                    value="__none"
                    onSelect={() => {
                      onChange(undefined)
                      close()
                    }}
                  >
                    Unassigned
                  </CommandItem>
                )}
                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => {
                      onChange(option)
                      close()
                    }}
                  >
                    <span className="truncate">{option.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      )}
    </PickerButton>
  )
}
